import React from 'react';
import { ArrowLeft, Shield, Database, Lock, Mail, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PrivacyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-paper">
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-amber-800 hover:text-amber-900 mb-6"
        >
          <ArrowLeft size={20} />
          Retour
        </button>

        {/* Header */}
        <header className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 mb-6">
            <Shield className="h-8 w-8 text-amber-800" />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl text-ink-primary mb-4">
            Politique de Confidentialité
          </h1>
          <p className="text-ink-secondary">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
          </p>
        </header>

        {/* Content */}
        <div className="prose prose-amber max-w-none space-y-8">
          
          {/* ÂGE MINIMUM - NOUVEAU */}
          <section className="bg-amber-50 border border-amber-200 rounded-xl p-6">
            <h2 className="font-heading text-xl text-amber-900 flex items-center gap-3 mb-4">
              <Shield className="h-5 w-5 text-amber-700" />
              Âge minimum requis
            </h2>
            <p className="text-amber-900 mb-4">
              Conformément à l'article 8 du Règlement Général sur la Protection des Données (RGPD) et à la législation française, <strong>cette application est réservée aux personnes âgées de 15 ans ou plus</strong>.
            </p>
            <ul className="space-y-2 text-amber-900">
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span>Si vous avez moins de 15 ans, vous ne pouvez pas créer de compte</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span>Votre majorité (18 ans ou plus) est confirmée lors de l'inscription</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span>Nous ne collectons pas de données personnelles d'enfants de moins de 15 ans</span>
              </li>
            </ul>
          </section>

          <section className="bg-paper-dark rounded-xl p-6">
            <h2 className="font-heading text-xl text-ink-primary flex items-center gap-3 mb-4">
              <Database className="h-5 w-5 text-amber-700" />
              Données collectées
            </h2>
            <p className="text-ink-secondary mb-4">
              L'application Bible Fillion collecte uniquement les données nécessaires à son fonctionnement :
            </p>
            <ul className="space-y-2 text-ink-secondary">
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span><strong>Email et mot de passe</strong> : Pour créer et accéder à votre compte personnel</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span><strong>Garde-pages</strong> : Vos positions de lecture sauvegardées</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span><strong>Surlignages</strong> : Les passages que vous avez surlignés avec leur couleur</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span><strong>Notes personnelles</strong> : Vos annotations sur les versets</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span><strong>Statut Premium</strong> : Si vous avez activé les fonctionnalités Premium</span>
              </li>
            </ul>
          </section>

          <section className="bg-paper-dark rounded-xl p-6">
            <h2 className="font-heading text-xl text-ink-primary flex items-center gap-3 mb-4">
              <Lock className="h-5 w-5 text-amber-700" />
              Utilisation des données
            </h2>
            <p className="text-ink-secondary mb-4">
              Vos données sont utilisées exclusivement pour :
            </p>
            <ul className="space-y-2 text-ink-secondary">
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span>Synchroniser vos données de lecture entre vos appareils</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span>Sauvegarder vos préférences et personnalisations</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span>Gérer votre abonnement Premium</span>
              </li>
            </ul>
            <p className="text-ink-secondary mt-4 font-medium">
              Nous ne vendons jamais vos données à des tiers et ne les utilisons pas à des fins publicitaires.
            </p>
          </section>

          <section className="bg-paper-dark rounded-xl p-6">
            <h2 className="font-heading text-xl text-ink-primary flex items-center gap-3 mb-4">
              <Shield className="h-5 w-5 text-amber-700" />
              Stockage et sécurité
            </h2>
            <p className="text-ink-secondary mb-4">
              Vos données sont stockées de manière sécurisée :
            </p>
            <ul className="space-y-2 text-ink-secondary">
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span>Hébergement sur <strong>Supabase</strong> (infrastructure cloud sécurisée)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span>Chiffrement des mots de passe (jamais stockés en clair)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span>Connexions sécurisées HTTPS</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span>Serveurs localisés en Europe (conformité RGPD)</span>
              </li>
            </ul>
          </section>

          <section className="bg-paper-dark rounded-xl p-6">
            <h2 className="font-heading text-xl text-ink-primary flex items-center gap-3 mb-4">
              <Trash2 className="h-5 w-5 text-amber-700" />
              Vos droits
            </h2>
            <p className="text-ink-secondary mb-4">
              Conformément au RGPD, vous disposez des droits suivants :
            </p>
            <ul className="space-y-2 text-ink-secondary">
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span><strong>Droit d'accès</strong> : Consulter toutes vos données personnelles</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span><strong>Droit de rectification</strong> : Modifier vos informations</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span><strong>Droit à l'effacement</strong> : Supprimer votre compte et toutes vos données</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span><strong>Droit à la portabilité</strong> : Exporter vos données</span>
              </li>
            </ul>
          </section>

          <section className="bg-paper-dark rounded-xl p-6">
            <h2 className="font-heading text-xl text-ink-primary flex items-center gap-3 mb-4">
              <Mail className="h-5 w-5 text-amber-700" />
              Contact
            </h2>
            <p className="text-ink-secondary">
              Pour toute question concernant vos données personnelles ou pour exercer vos droits, 
              contactez-nous à : <a href="mailto:contact@bible-fillion.app" className="text-amber-700 hover:text-amber-900 underline">contact@bible-fillion.app</a>
            </p>
          </section>

          <section className="text-center py-8">
            <p className="text-ink-secondary text-sm italic">
              « La Bible est le livre de Notre-Seigneur Jésus-Christ. » — L.-Cl. Fillion
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
